import React, { useState, useEffect, useRef, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import { useAuth } from '@/lib/AuthContext';
import { Link } from 'react-router-dom';
import {
  Loader2, ShieldOff, Upload, Plus, Building2, FileText, ChevronDown, ChevronUp,
  CheckCircle, AlertCircle, X, Edit2, Save, ExternalLink, Package, Trash2,
  FileSpreadsheet, Mail, ShieldCheck, Eye, Download, ArrowLeft,
} from 'lucide-react';
import ConfirmDialog from '@/components/admin/ConfirmDialog';
import { useToast } from '@/components/ui/use-toast';
import InvoiceReconciliationTab from '@/components/admin/b2b/InvoiceReconciliationTab';
import B2BCreditCheckPanel from '@/components/admin/b2b/B2BCreditCheckPanel';
import ConsumerAccountsTab from '@/components/admin/b2b/ConsumerAccountsTab';
import AccountDetailPanel from '@/components/admin/b2b/AccountDetailPanel';
import FulfilmentStageControls from '@/components/admin/b2b/FulfilmentStageControls';
import SquareStockPanel from '@/components/admin/b2b/SquareStockPanel';
import SquareSyncTag from '@/components/admin/b2b/SquareSyncTag';
import { sortB2BOrders, fmtB2BOrderDate } from '@/lib/b2bOrderDate';
import B2BHowItWorks from '@/components/admin/b2b/B2BHowItWorks';
import useResilientSubscription from '@/hooks/useResilientSubscription';

const STATUS_COLORS = {
  draft: 'text-white/40 border-white/15',
  acknowledged: 'text-blue-400 border-blue-400/30',
  in_production: 'text-yellow-400 border-yellow-400/30',
  stock_allocated: 'text-cyan-400 border-cyan-400/30',
  dispatched: 'text-purple-400 border-purple-400/30',
  invoiced: 'text-orange-400 border-orange-400/30',
  paid: 'text-green-400 border-green-400/30',
  cancelled: 'text-red-400 border-red-400/30',
  unallocated: 'text-yellow-400 border-yellow-400/30',
};

const STATUS_OPTIONS = ['draft','acknowledged','in_production','stock_allocated','dispatched','invoiced','paid','unallocated','cancelled'];

export default function AdminB2B() {
  const { user, isLoadingAuth } = useAuth();
  const { toast } = useToast();
  const [accounts, setAccounts] = useState([]);
  const [orders, setOrders] = useState([]);
  // POs that arrived on this screen while it was open — badged until opened.
  const [newOrderIds, setNewOrderIds] = useState(() => new Set());
  const [consumerAccounts, setConsumerAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('orders'); // 'orders' | 'accounts' | 'reconcile' | 'consumers'
  const [dragging, setDragging] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsedOrder, setParsedOrder] = useState(null);
  const [parsedPdfUrl, setParsedPdfUrl] = useState('');
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [prefillAccount, setPrefillAccount] = useState(null);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(null);
  const dropRef = useRef(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [accs, ords, consumers] = await Promise.all([
        base44.entities.B2BAccount.list('-created_date', 500),
        base44.entities.B2BOrder.list('-created_date', 500),
        base44.entities.ConsumerAccount.list('-created_date', 500),
      ]);
      setAccounts(accs || []);
      setOrders(ords || []);
      setConsumerAccounts(consumers || []);
    } catch (e) {
      if (!silent) toast({ title: 'Failed to load B2B data', description: e.message, variant: 'destructive' });
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // A PO uploaded by a trade customer has to land on this screen by itself —
  // staff shouldn't have to reload to notice it. Live events are merged into the
  // loaded list rather than refetching everything; the resilient hook re-reads
  // the list whenever the socket goes quiet, so the screen can never sit on
  // stale data if live events are unavailable.
  const onOrderEvent = useCallback((event) => {
    const record = event?.data;
    if (!record?.id) return;
    if (event.type === 'delete') {
      setOrders((prev) => prev.filter((o) => o.id !== record.id));
      return;
    }
    setOrders((prev) => {
      const exists = prev.some((o) => o.id === record.id);
      if (!exists) return [record, ...prev];
      return prev.map((o) => (o.id === record.id ? { ...o, ...record } : o));
    });
    if (event.type === 'create') {
      setNewOrderIds((prev) => new Set(prev).add(record.id));
    }
  }, []);

  useResilientSubscription({
    entities: ['B2BOrder'],
    enabled: !!user && user.role === 'admin',
    onEvent: onOrderEvent,
    onResync: () => load({ silent: true }),
    // A quiet list is swept once a minute — frequent enough that a missed event
    // is caught quickly, quiet enough not to hammer the API on an idle screen.
    pollMs: 60000,
  });

  const clearNewOrder = useCallback((id) => {
    setNewOrderIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  if (isLoadingAuth) return <div className="min-h-screen bg-[#0d0d0f] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  if (!user || user.role !== 'admin') return (
    <div className="min-h-screen bg-[#0d0d0f] flex items-center justify-center">
      <div className="text-center"><ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" /><p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
      <Link to="/admin" className="inline-block mt-5 text-primary text-xs font-bold tracking-widest hover:underline">SIGN IN →</Link></div>
    </div>
  );

  const handleFileDrop = async (file) => {
    if (!file || file.type !== 'application/pdf') { toast({ title: 'Please upload a PDF file', variant: 'destructive' }); return; }
    setParsing(true);
    setParsedOrder(null);
    try {
      // Upload PDF first
      const upRes = await base44.integrations.Core.UploadFile({ file });
      if (!upRes?.file_url) throw new Error('Upload failed');
      setParsedPdfUrl(upRes.file_url);
      // Parse with LLM
      const res = await base44.functions.invoke('b2bParsePO', { pdf_url: upRes.file_url });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Parse failed');
      setParsedOrder(res.data.data);
    } catch (e) {
      toast({ title: 'Parse failed', description: e.message, variant: 'destructive' });
    }
    setParsing(false);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleFileDrop(file);
  };

  const handleFileInput = (e) => { handleFileDrop(e.target.files?.[0]); e.target.value = ''; };

  const saveOrder = async (parsed, accountId) => {
    const account = accounts.find(a => a.id === accountId);
    const discPct = Number(account?.discount_percent) || 0;
    const lineItems = (parsed.line_items || []).map(li => {
      const base = { ...li, stock_allocated: false };
      if (discPct > 0) {
        const ext = Math.round((Number(li.extended_price) || 0) * (1 - discPct / 100) * 100) / 100;
        const unit = Number(li.qty) ? Math.round(ext / li.qty * 100) / 100 : ext;
        return { ...base, extended_price: ext, unit_price: unit };
      }
      return base;
    });
    const subtotal = lineItems.reduce((s, li) => s + (li.extended_price || 0), 0);
    try {
      const order = await base44.entities.B2BOrder.create({
        b2b_account_id: accountId,
        company_name: account?.company_name || parsed.company_name,
        po_number: parsed.po_number,
        po_date: parsed.po_date,
        status: 'acknowledged',
        line_items: lineItems,
        subtotal_ex_vat: Math.round(subtotal * 100) / 100,
        vat_amount: Math.round(subtotal * 0.2 * 100) / 100,
        total_inc_vat: Math.round(subtotal * 1.2 * 100) / 100,
        delivery_address: parsed.delivery_address,
        source_pdf_url: parsedPdfUrl,
        notes: discPct > 0 ? `Trade discount of ${discPct}% applied to all line items.` : undefined,
      });
      setOrders(prev => [order, ...prev]);
      setParsedOrder(null);
      setParsedPdfUrl('');
      toast({ title: 'Order created', description: `PO ${parsed.po_number} saved` });
    } catch (e) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    }
  };

  const updateOrderStatus = async (id, status) => {
    // Cancelling an order that has already been invoiced also returns its stock
    // to Square, so that path runs through the backend. Every other status
    // change is a plain field update.
    if (status === 'cancelled') {
      try {
        const res = await base44.functions.invoke('b2bCancelOrder', { order_id: id, status });
        if (!res?.data?.success) throw new Error(res?.data?.error || 'Cancellation failed');
        const updated = res.data.order;
        setOrders(prev => prev.map(o => o.id === id ? { ...o, ...(updated || {}), status } : o));
        const warnings = res.data.square?.warnings || [];
        toast({
          title: 'Order cancelled',
          description: warnings[0]
            || (res.data.square?.posted ? `${res.data.square.posted} Square adjustment(s) returned to stock` : 'No stock to return to Square'),
        });
      } catch (e) {
        toast({ title: 'Cancellation failed', description: e.message, variant: 'destructive' });
      }
      return;
    }
    try {
      await base44.entities.B2BOrder.update(id, { status });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
    } catch (e) {
      toast({ title: 'Status update failed', description: e.message, variant: 'destructive' });
    }
  };

  // Manually stage (or unstage) a parsed PO for the Sage Audit Trail export
  // queue. Staged orders (sage_export_ready=true) appear in the Sage export
  // list; unstaged ones stay out until an admin reconciles them — no
  // auto-staging.
  const stageForSage = async (id, ready) => {
    try {
      await base44.entities.B2BOrder.update(id, { sage_export_ready: ready });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, sage_export_ready: ready } : o));
      toast(ready
        ? { title: 'Staged for Sage export', description: 'This PO now appears in the Sage export queue (Admin → Invoices → Sage Export).' }
        : { title: 'Removed from Sage queue', description: 'This PO will no longer appear in the Sage export list.' });
    } catch (e) {
      toast({ title: 'Update failed', description: e.message, variant: 'destructive' });
    }
  };

  const toggleStockLine = async (order, idx) => {
    const newItems = order.line_items.map((li, i) => i === idx ? { ...li, stock_allocated: !li.stock_allocated } : li);
    const allAllocated = newItems.every(li => li.stock_allocated);
    try {
      await base44.entities.B2BOrder.update(order.id, {
        line_items: newItems,
        ...(allAllocated ? { status: 'stock_allocated' } : {}),
      });
      setOrders(prev => prev.map(o => o.id === order.id ? { ...o, line_items: newItems, ...(allAllocated ? { status: 'stock_allocated' } : {}) } : o));
    } catch (e) {
      toast({ title: 'Stock update failed', description: e.message, variant: 'destructive' });
    }
  };

  const generateInvoice = async (orderId) => {
    setGeneratingInvoice(orderId);
    try {
      const res = await base44.functions.invoke('b2bGenerateInvoice', { order_id: orderId });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Invoice generation failed');
      const updated = res.data.order;
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...(updated || {}), invoice_url: res.data.invoice_url, invoice_number: res.data.invoice_number, status: 'invoiced' } : o));
      const warnings = res.data.square?.warnings || [];
      toast(warnings.length
        ? { title: 'Invoice generated — stock warning', description: warnings[0] }
        : { title: 'Invoice generated', description: res.data.invoice_number });
    } catch (e) {
      toast({ title: 'Invoice failed', description: e.message, variant: 'destructive' });
    }
    setGeneratingInvoice(null);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <header className="py-12 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-xs font-bold tracking-widest mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div className="flex items-center gap-3 mb-2"><Building2 className="w-6 h-6 text-primary" /><p className="text-primary text-xs font-bold tracking-widest">ORDERS &amp; CUSTOMERS</p></div>
          <h1 className="text-white font-black text-2xl sm:text-4xl uppercase">B2B Trade Portal</h1>
          <p className="text-white/30 text-sm mt-1">Upload purchase orders, manage trade accounts, allocate stock and generate invoices.</p>
        </div>
      </header>

      <section className="px-6 lg:px-10 py-8 max-w-6xl mx-auto">
        <B2BHowItWorks />

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10">
          {[{ key: 'orders', label: 'Purchase Orders' }, { key: 'accounts', label: 'Trade Accounts' }, { key: 'consumers', label: 'Consumer Accounts' }, { key: 'reconcile', label: 'Data Import / Export' }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-5 py-3 text-xs font-bold tracking-widest -mb-px border-b-2 transition-colors ${tab === t.key ? 'border-primary text-primary' : 'border-transparent text-white/40 hover:text-white/70'}`}>
              {t.label.toUpperCase()}
            </button>
          ))}
        </div>

        {tab === 'orders' && (
          <OrdersTab
            orders={orders} accounts={accounts} loading={loading}
            dragging={dragging} setDragging={setDragging} parsing={parsing}
            parsedOrder={parsedOrder} setParsedOrder={setParsedOrder}
            onDrop={onDrop} handleFileInput={handleFileInput}
            saveOrder={saveOrder} updateOrderStatus={updateOrderStatus}
            toggleStockLine={toggleStockLine} generateInvoice={generateInvoice}
            generatingInvoice={generatingInvoice} expandedOrder={expandedOrder}
            setExpandedOrder={setExpandedOrder} dropRef={dropRef}
            setShowNewAccount={setShowNewAccount} setTab={setTab}
            setPrefillAccount={setPrefillAccount} stageForSage={stageForSage}
            newOrderIds={newOrderIds} onOpenOrder={clearNewOrder}
            onOrderUpdated={(updated) => setOrders(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o))}
          />
        )}

        {tab === 'accounts' && (
          <AccountsTab
            accounts={accounts} setAccounts={setAccounts}
            orders={orders}
            onOrderUpdated={(updated) => setOrders(prev => prev.map(o => o.id === updated.id ? { ...o, ...updated } : o))}
            showNewAccount={showNewAccount} setShowNewAccount={setShowNewAccount}
            prefillAccount={prefillAccount} setPrefillAccount={setPrefillAccount}
            toast={toast}
          />
        )}

        {tab === 'consumers' && (
          <ConsumerAccountsTab
            consumerAccounts={consumerAccounts}
            setConsumerAccounts={setConsumerAccounts}
            orders={orders}
          />
        )}

        {tab === 'reconcile' && (
          <InvoiceReconciliationTab
            accounts={accounts}
            onOrdersChanged={load}
            onAccountsChanged={(created) => setAccounts(prev => [created, ...prev])}
          />
        )}
      </section>
    </div>
  );
}

// ─── ORDERS TAB ──────────────────────────────────────────────────────────────
function OrdersTab({
  orders, accounts, loading, dragging, setDragging, parsing, parsedOrder, setParsedOrder,
  onDrop, handleFileInput, saveOrder, updateOrderStatus, toggleStockLine,
  generateInvoice, generatingInvoice, expandedOrder, setExpandedOrder, dropRef,
  setShowNewAccount, setTab, setPrefillAccount, stageForSage, onOrderUpdated,
  newOrderIds, onOpenOrder,
}) {
  return (
    <div className="space-y-6">
      {/* Drop zone */}
      <div
        ref={dropRef}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded transition-colors p-8 text-center ${dragging ? 'border-primary bg-primary/5' : 'border-white/15 hover:border-white/30'}`}
      >
        {parsing ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-white/50 text-sm font-bold tracking-widest">PARSING PURCHASE ORDER…</p>
            <p className="text-white/30 text-xs">AI is extracting order data from your PDF</p>
          </div>
        ) : (
          <label className="cursor-pointer flex flex-col items-center gap-3">
            <Upload className="w-8 h-8 text-white/30" />
            <p className="text-white/70 font-bold text-sm tracking-widest">DROP A PURCHASE ORDER PDF HERE</p>
            <p className="text-white/30 text-xs">Or click to browse — AI will extract all line items automatically</p>
            <input type="file" accept="application/pdf" className="hidden" onChange={handleFileInput} />
          </label>
        )}
      </div>

      {/* Parsed order review panel */}
      {parsedOrder && (
        <ParsedOrderPanel
          parsed={parsedOrder} accounts={accounts}
          onSave={saveOrder} onDiscard={() => setParsedOrder(null)}
          setShowNewAccount={setShowNewAccount} setTab={setTab}
          setPrefillAccount={setPrefillAccount}
        />
      )}

      {/* Orders list */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 text-white/30">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-bold tracking-widest">NO PURCHASE ORDERS YET</p>
          <p className="text-xs mt-1">Upload a PDF above to create your first B2B order</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sortB2BOrders(orders).map(order => (
            <OrderRow
              key={order.id} order={order} accounts={accounts}
              expanded={expandedOrder === order.id}
              isNew={newOrderIds?.has(order.id)}
              onToggle={() => {
                onOpenOrder(order.id);
                setExpandedOrder(expandedOrder === order.id ? null : order.id);
              }}
              onStatusChange={updateOrderStatus}
              onToggleStock={toggleStockLine}
              onGenerateInvoice={generateInvoice}
              generatingInvoice={generatingInvoice}
              onStageForSage={stageForSage}
              onOrderUpdated={onOrderUpdated}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PARSED ORDER REVIEW ─────────────────────────────────────────────────────
function ParsedOrderPanel({ parsed, accounts, onSave, onDiscard, setShowNewAccount, setTab, setPrefillAccount }) {
  const [selectedAccount, setSelectedAccount] = useState('');
  const [editedParsed, setEditedParsed] = useState(parsed);

  useEffect(() => {
    // Auto-match by company name or account code
    const match = accounts.find(a =>
      a.company_name?.toLowerCase() === parsed.company_name?.toLowerCase() ||
      (parsed.account_code && a.account_code?.toLowerCase() === parsed.account_code?.toLowerCase())
    );
    if (match) setSelectedAccount(match.id);
  }, [parsed, accounts]);

  const updateLine = (idx, field, value) => {
    setEditedParsed(prev => ({
      ...prev,
      line_items: prev.line_items.map((li, i) => i === idx ? { ...li, [field]: field === 'qty' || field === 'unit_price' || field === 'extended_price' ? Number(value) : value } : li),
    }));
  };

  const subtotal = (editedParsed.line_items || []).reduce((s, li) => s + (li.extended_price || 0), 0);
  const selAccount = accounts.find(a => a.id === selectedAccount);
  const discPct = Number(selAccount?.discount_percent) || 0;
  const discountAmount = Math.round(subtotal * discPct / 100 * 100) / 100;
  const netSubtotal = Math.round((subtotal - discountAmount) * 100) / 100;

  return (
    <div className="border border-primary/30 bg-primary/[0.03] p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-primary text-[10px] font-bold tracking-widest mb-1">PARSED PURCHASE ORDER</p>
          <p className="text-white font-bold text-lg">{parsed.company_name} — PO {parsed.po_number}</p>
          <p className="text-white/40 text-xs mt-0.5">{parsed.po_date} · {(parsed.line_items || []).length} line items · £{(editedParsed.subtotal_ex_vat || subtotal).toFixed(2)} ex. VAT</p>
        </div>
        <button onClick={onDiscard} className="text-white/30 hover:text-white p-1"><X className="w-5 h-5" /></button>
      </div>

      {/* Account selector */}
      <div>
        <label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">ASSIGN TO TRADE ACCOUNT</label>
        {accounts.length === 0 ? (
          <p className="text-white/40 text-xs">No accounts yet —{' '}
            <button onClick={() => { setPrefillAccount(parsed); setTab('accounts'); setShowNewAccount(true); }} className="text-primary hover:underline">create one first</button>
          </p>
        ) : (
          <select
            value={selectedAccount}
            onChange={e => setSelectedAccount(e.target.value)}
            className="seo-input w-full max-w-sm"
          >
            <option value="">— Select account —</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.company_name}{a.account_code ? ` (${a.account_code})` : ''}</option>)}
          </select>
        )}
        {!selectedAccount && parsed.company_name && (
          <p className="text-yellow-400 text-xs mt-2 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" />
            No account matched for "{parsed.company_name}" — select one or{' '}
            <button onClick={() => { setPrefillAccount(parsed); setTab('accounts'); setShowNewAccount(true); }} className="underline">create a new account</button>
          </p>
        )}
        {selectedAccount && <p className="text-green-400 text-xs mt-2 flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5" /> Matched to account</p>}
      </div>

      {/* Line items review table */}
      <div>
        <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">LINE ITEMS — REVIEW &amp; EDIT</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-white/30 font-bold text-left py-2 pr-3 w-8">#</th>
                <th className="text-white/30 font-bold text-left py-2 pr-3">Code</th>
                <th className="text-white/30 font-bold text-left py-2 pr-3">Description</th>
                <th className="text-white/30 font-bold text-right py-2 pr-3 w-16">Qty</th>
                <th className="text-white/30 font-bold text-right py-2 pr-3 w-24">Unit Price</th>
                <th className="text-white/30 font-bold text-right py-2 w-24">Ext. Price</th>
              </tr>
            </thead>
            <tbody>
              {(editedParsed.line_items || []).map((li, idx) => (
                <tr key={idx} className="border-b border-white/5">
                  <td className="text-white/40 py-2 pr-3">{li.item_no}</td>
                  <td className="py-2 pr-3">
                    <input value={li.product_code || ''} onChange={e => updateLine(idx, 'product_code', e.target.value)} className="seo-input w-full" />
                  </td>
                  <td className="py-2 pr-3">
                    <input value={li.description || ''} onChange={e => updateLine(idx, 'description', e.target.value)} className="seo-input w-full" />
                  </td>
                  <td className="py-2 pr-3">
                    <input type="number" value={li.qty || ''} onChange={e => updateLine(idx, 'qty', e.target.value)} className="seo-input w-full text-right" />
                  </td>
                  <td className="py-2 pr-3">
                    <input type="number" step="0.01" value={li.unit_price || ''} onChange={e => updateLine(idx, 'unit_price', e.target.value)} className="seo-input w-full text-right" />
                  </td>
                  <td className="py-2">
                    <input type="number" step="0.01" value={li.extended_price || ''} onChange={e => updateLine(idx, 'extended_price', e.target.value)} className="seo-input w-full text-right" />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-white/20">
                <td colSpan={5} className="text-right text-white/50 font-bold py-2 pr-3 text-xs">Subtotal (ex. VAT)</td>
                <td className="text-right text-white font-bold py-2">£{subtotal.toFixed(2)}</td>
              </tr>
              {discPct > 0 && (
                <>
                  <tr>
                    <td colSpan={5} className="text-right text-green-400 py-1 pr-3 text-xs">Trade discount ({discPct}%)</td>
                    <td className="text-right text-green-400 py-1">-£{discountAmount.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="text-right text-white/50 font-bold py-1 pr-3 text-xs">Net subtotal (ex. VAT)</td>
                    <td className="text-right text-white font-bold py-1">£{netSubtotal.toFixed(2)}</td>
                  </tr>
                </>
              )}
            </tfoot>
          </table>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => onSave(editedParsed, selectedAccount)}
          disabled={!selectedAccount}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-40 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors"
        >
          <Save className="w-4 h-4" /> SAVE ORDER
        </button>
        <button onClick={onDiscard} className="border border-white/10 hover:border-white/25 text-white/50 hover:text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors">
          DISCARD
        </button>
      </div>
    </div>
  );
}

// ─── ORDER ROW ───────────────────────────────────────────────────────────────
function OrderRow({ order, accounts, expanded, isNew, onToggle, onStatusChange, onToggleStock, onGenerateInvoice, generatingInvoice, onStageForSage, onOrderUpdated }) {
  const [staging, setStaging] = useState(false);
  const account = accounts.find(a => a.id === order.b2b_account_id);
  const totalLines = (order.line_items || []).length;
  const allocatedLines = (order.line_items || []).filter(li => li.stock_allocated).length;
  // Per-line Square outcome, matched back to its row by line index.
  const syncByIndex = new Map((order.square_stock_deduction || []).map(r => [r.line_index, r]));

  return (
    <div className={`border bg-[#111115] ${isNew ? 'border-primary/50 bg-primary/[0.04]' : 'border-white/10'}`}>
      <div className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-white/[0.02] transition-colors" onClick={onToggle}>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-white font-bold text-sm">{order.company_name}</p>
            <span className={`px-2 py-0.5 border text-[10px] font-bold tracking-widest uppercase ${STATUS_COLORS[order.status] || 'text-white/40 border-white/15'}`}>{(order.status || 'draft').replace('_', ' ')}</span>
            {isNew && <span className="bg-primary text-white text-[10px] font-bold tracking-widest px-1.5 py-0.5">NEW</span>}
            {order.po_number && <span className="text-white/40 text-xs font-mono">PO {order.po_number}</span>}
            {order.sage_export_ready && !order.sage_invoice_number && <span className="text-cyan-400 text-[10px] font-bold tracking-widest border border-cyan-400/30 bg-cyan-400/10 px-1.5 py-0.5">SAGE-READY</span>}
          </div>
          <p className="text-white/30 text-xs mt-0.5">
            {fmtB2BOrderDate(order)} · {totalLines} items · £{(order.subtotal_ex_vat || 0).toFixed(2)} ex. VAT
            {allocatedLines > 0 && <span className="ml-2 text-cyan-400">{allocatedLines}/{totalLines} allocated</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {order.invoice_url && (
            <a href={order.invoice_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/50 hover:text-white text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors">
              <FileText className="w-3.5 h-3.5" /> INVOICE
            </a>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-white/10 p-5 space-y-5">
          {/* Status + controls */}
          <div className="flex flex-wrap gap-3 items-center">
            <div>
              <label className="text-white/30 text-[10px] font-bold tracking-widest mb-1 block">STATUS</label>
              <select value={order.status} onChange={e => onStatusChange(order.id, e.target.value)} className="seo-input">
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
            {order.source_pdf_url && (
              <a href={order.source_pdf_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/25 text-white/50 hover:text-white font-bold text-[10px] tracking-widest px-3 py-2 transition-colors mt-auto">
                <ExternalLink className="w-3.5 h-3.5" /> VIEW ORIGINAL PO
              </a>
            )}
            <button
              onClick={() => onGenerateInvoice(order.id)}
              disabled={generatingInvoice === order.id}
              className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/30 hover:bg-primary hover:text-white text-primary font-bold text-[10px] tracking-widest px-3 py-2 transition-colors mt-auto disabled:opacity-50"
            >
              {generatingInvoice === order.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
              {order.invoice_url ? 'REGENERATE INVOICE' : 'GENERATE INVOICE'}
            </button>
            {onStageForSage && !order.sage_invoice_number && (
              order.sage_export_ready ? (
                <button
                  onClick={() => { setStaging(true); onStageForSage(order.id, false).finally(() => setStaging(false)); }}
                  disabled={staging}
                  title="Remove this PO from the Sage export queue"
                  className="inline-flex items-center gap-1.5 border border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400/20 text-cyan-400 font-bold text-[10px] tracking-widest px-3 py-2 transition-colors mt-auto disabled:opacity-50"
                >
                  {staging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />} UNSTAGE
                </button>
              ) : (
                <button
                  onClick={() => { setStaging(true); onStageForSage(order.id, true).finally(() => setStaging(false)); }}
                  disabled={staging}
                  title="Stage this PO for the Sage Audit Trail export & reconciliation queue"
                  className="inline-flex items-center gap-1.5 border border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black text-cyan-400 font-bold text-[10px] tracking-widest px-3 py-2 transition-colors mt-auto disabled:opacity-50"
                >
                  {staging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} RECONCILE THIS PO
                </button>
              )
            )}
          </div>

          {/* Square stock sync — warnings + per-line outcome */}
          <SquareStockPanel order={order} />

          {/* Fulfilment stage tracker + advancement */}
          <FulfilmentStageControls order={order} onStageAdvanced={onOrderUpdated} />

          {/* Line items + stock allocation */}
          <div>
            <p className="text-white/40 text-[10px] font-bold tracking-widest mb-2">LINE ITEMS — STOCK ALLOCATION</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-white/30 text-left py-2 pr-3 w-8">#</th>
                    <th className="text-white/30 text-left py-2 pr-3">Code</th>
                    <th className="text-white/30 text-left py-2 pr-3">Description</th>
                    <th className="text-white/30 text-right py-2 pr-3">Qty</th>
                    <th className="text-white/30 text-right py-2 pr-3">Unit Price</th>
                    <th className="text-white/30 text-right py-2 pr-3">Ext. Price</th>
                    <th className="text-white/30 text-center py-2">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.line_items || []).map((li, idx) => (
                    <tr key={idx} className={`border-b border-white/5 ${li.stock_allocated ? 'bg-cyan-400/5' : ''}`}>
                      <td className="text-white/40 py-2 pr-3">{li.item_no}</td>
                      <td className="text-white/70 font-mono py-2 pr-3">{li.product_code}</td>
                      <td className="text-white py-2 pr-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{li.description}</span>
                          <SquareSyncTag result={syncByIndex.get(idx)} />
                        </div>
                      </td>
                      <td className="text-white text-right py-2 pr-3">{li.qty}</td>
                      <td className="text-white/70 text-right py-2 pr-3">£{Number(li.unit_price || 0).toFixed(2)}</td>
                      <td className="text-white font-bold text-right py-2 pr-3">£{Number(li.extended_price || 0).toFixed(2)}</td>
                      <td className="text-center py-2">
                        <button
                          onClick={() => onToggleStock(order, idx)}
                          className={`inline-flex items-center gap-1 px-2 py-1 border text-[10px] font-bold tracking-wider transition-colors ${li.stock_allocated ? 'border-cyan-400/40 bg-cyan-400/10 text-cyan-400' : 'border-white/15 text-white/40 hover:border-white/30 hover:text-white'}`}
                        >
                          <Package className="w-3 h-3" />
                          {li.stock_allocated ? 'ALLOCATED' : 'ALLOCATE'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-white/20">
                    <td colSpan={5} className="text-right text-white/50 text-xs py-2 pr-3 font-bold">Subtotal ex. VAT</td>
                    <td colSpan={2} className="text-right text-white font-bold text-xs py-2 pr-3">£{(order.subtotal_ex_vat || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="text-right text-white/50 text-xs py-1 pr-3">VAT (20%)</td>
                    <td colSpan={2} className="text-right text-white/70 text-xs py-1 pr-3">£{(order.vat_amount || 0).toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="text-right text-white/50 text-xs py-1 pr-3 font-bold">Total inc. VAT</td>
                    <td colSpan={2} className="text-right text-white font-bold py-1 pr-3">£{(order.total_inc_vat || 0).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Delivery address */}
          {order.delivery_address && (
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">DELIVERY ADDRESS</p>
              <p className="text-white/60 text-xs whitespace-pre-line">{order.delivery_address}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ACCOUNTS TAB ─────────────────────────────────────────────────────────────
function AccountsTab({ accounts, setAccounts, orders, onOrderUpdated, showNewAccount, setShowNewAccount, prefillAccount, setPrefillAccount, toast }) {
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [checkingCredit, setCheckingCredit] = useState(null);
  const [viewAccount, setViewAccount] = useState(null);
  const [sortKey, setSortKey] = useState('account_code');
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  const sortedAccounts = [...accounts].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'account_code') return String(a.account_code || '').localeCompare(String(b.account_code || '')) * dir;
    if (sortKey === 'company_name') return String(a.company_name || '').localeCompare(String(b.company_name || '')) * dir;
    if (sortKey === 'contact_name') return String(a.contact_name || '').localeCompare(String(b.contact_name || '')) * dir;
    if (sortKey === 'payment_terms') return (Number(a.payment_terms_days) || 0 - (Number(b.payment_terms_days) || 0)) * dir;
    if (sortKey === 'balance') return (Number(a.balance) - Number(b.balance)) * dir;
    return 0;
  });

  const [exporting, setExporting] = useState(null);
  const exportToSage = async (account) => {
    setExporting(account.id);
    try {
      const res = await base44.functions.invoke('exportCustomerToSage', { account_type: 'b2b', account_id: account.id });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Export failed');
      const blob = new Blob([res.data.csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = res.data.filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast({ title: 'Sage customer CSV exported', description: account.company_name });
    } catch (e) {
      toast({ title: 'Export failed', description: e.message, variant: 'destructive' });
    }
    setExporting(null);
  };

  const runCreditCheck = async (id) => {
    setCheckingCredit(id);
    try {
      const res = await base44.functions.invoke('b2bRunCreditCheck', { account_id: id });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Credit check failed');
      setAccounts(prev => prev.map(a => a.id === id ? { ...a, credit_check: res.data.credit_check } : a));
      toast({ title: 'Credit check complete', description: `${res.data.credit_check.status} · ${res.data.credit_check.risk_band} risk` });
    } catch (e) {
      toast({ title: 'Credit check failed', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    }
    setCheckingCredit(null);
  };

  // Map a parsed purchase order into the account form fields, including a
  // best-effort split of the delivery address into street / town / postcode.
  const deriveCode = (name) => {
    const base = String(name || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase();
    return base ? `${base}001` : '';
  };
  const poToAccountPrefill = (p) => {
    const lines = String(p.delivery_address || '').split(/\n/).map(l => l.trim()).filter(Boolean);
    const pcMatch = String(p.delivery_address || '').match(/\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/i);
    const postcode = pcMatch ? `${pcMatch[1]} ${pcMatch[2]}`.toUpperCase() : '';
    let city = '';
    const addrLines = [...lines];
    if (pcMatch) {
      const idx = addrLines.findIndex(l => l.replace(/\s/g, '').toUpperCase().includes(postcode.replace(/\s/g, '')));
      if (idx >= 0) { city = addrLines[idx].replace(new RegExp(pcMatch[0], 'i'), '').trim(); addrLines.splice(idx, 1); }
    }
    if (!city && addrLines.length >= 3) city = addrLines[addrLines.length - 1];
    return {
      company_name: p.company_name || '',
      account_code: p.account_code || deriveCode(p.company_name),
      company_reg: p.company_reg || '',
      vat_number: p.vat_number || '',
      contact_name: p.contact_name || '',
      contact_email: p.contact_email || '',
      contact_phone: p.contact_phone || '',
      website: p.website || '',
      address_line1: addrLines[0] || '',
      address_line2: addrLines.slice(1).join(', '),
      address_city: city,
      address_postcode: postcode,
      address_country: 'United Kingdom',
    };
  };

  const inviteAccount = async (id) => {
    setInviting(id);
    try {
      const res = await base44.functions.invoke('b2bInviteCustomer', { account_id: id });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Invite failed');
      setAccounts(prev => prev.map(a => a.id === id ? { ...a, invited_at: new Date().toISOString(), portal_email: res.data.email } : a));
      toast({ title: 'Invite sent', description: `Set-up email sent to ${res.data.email}` });
    } catch (e) {
      toast({ title: 'Invite failed', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    }
    setInviting(null);
  };

  const BLANK = {
    company_name: '', account_code: '', company_reg: '', balance: 0, inactive: false,
    credit_limit_gbp: null,
    address_line1: '', address_line2: '', address_city: '', address_county: '', address_postcode: '', address_country: 'United Kingdom',
    vat_number: '', eori_number: '', ukims_number: '',
    contact_name: '', trade_contact: '', contact_email: '', secondary_emails: [], email_statements: false,
    contact_phone: '', phone_2: '', fax: '', website: '',
    twitter: '', linkedin: '', facebook: '',
    payment_terms_days: 30, discount_percent: 0, notes: '', active: true,
  };

  useEffect(() => {
    if (showNewAccount) {
      setForm(prefillAccount ? { ...BLANK, ...poToAccountPrefill(prefillAccount) } : BLANK);
      setEditId('new');
      if (setPrefillAccount) setPrefillAccount(null);
    }
  }, [showNewAccount, prefillAccount]);

  const save = async () => {
    if (!form.company_name?.trim()) { toast({ title: 'Company name required', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const payload = { ...form, active: !form.inactive };
      if (editId === 'new') {
        const created = await base44.entities.B2BAccount.create(payload);
        setAccounts(prev => [created, ...prev]);
        toast({ title: 'Account created' });
      } else {
        await base44.entities.B2BAccount.update(editId, payload);
        setAccounts(prev => prev.map(a => a.id === editId ? { ...a, ...payload } : a));
        toast({ title: 'Account updated' });
      }
      setEditId(null);
      setShowNewAccount(false);
    } catch (e) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    }
    setSaving(false);
  };

  const deleteAccount = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await base44.entities.B2BAccount.delete(deleteTarget.id);
      setAccounts(prev => prev.filter(a => a.id !== deleteTarget.id));
      toast({ title: 'Account deleted', description: deleteTarget.company_name });
      setDeleteTarget(null);
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
    setDeleting(false);
  };

  // Full account detail view replaces the list until the admin goes back
  if (viewAccount) {
    const current = accounts.find((a) => a.id === viewAccount.id) || viewAccount;
    return (
      <div className="max-w-5xl">
        <AccountDetailPanel account={current} orders={orders} onBack={() => setViewAccount(null)} onAccountUpdated={(updated) => setAccounts(prev => prev.map(a => a.id === updated.id ? { ...a, ...updated } : a))} onOrderUpdated={onOrderUpdated} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => { setForm(BLANK); setEditId('new'); setShowNewAccount(true); }}
          className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors">
          <Plus className="w-4 h-4" /> NEW ACCOUNT
        </button>
      </div>

      {(editId === 'new' || editId) && (
        <AccountForm form={form} setForm={setForm} onSave={save} onCancel={() => { setEditId(null); setShowNewAccount(false); }} saving={saving} isNew={editId === 'new'} />
      )}

      {accounts.length === 0 && !editId ? (
        <div className="text-center py-16 text-white/30">
          <Building2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-bold tracking-widest">NO TRADE ACCOUNTS YET</p>
          <p className="text-xs mt-1">Create an account to start managing B2B orders</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 pb-1">
            <span className="text-white/30 text-[10px] font-bold tracking-widest mr-1">SORT BY</span>
            {[
              { key: 'account_code', label: 'ACCOUNT CODE' },
              { key: 'company_name', label: 'COMPANY' },
              { key: 'contact_name', label: 'CONTACT' },
              { key: 'payment_terms', label: 'TERMS' },
              { key: 'balance', label: 'BALANCE' },
            ].map((h) => (
              <button
                key={h.key}
                onClick={() => toggleSort(h.key)}
                className={`px-2.5 py-1 text-[10px] font-bold tracking-widest border transition-colors ${sortKey === h.key ? 'border-primary bg-primary/10 text-primary' : 'border-white/10 text-white/40 hover:text-white hover:border-white/25'}`}
              >
                {h.label}{sortKey === h.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
              </button>
            ))}
          </div>
          {sortedAccounts.map(account => (
            <div key={account.id} className="border border-white/10 bg-[#111115]">
              <div className="p-5 flex items-start gap-4">
                <Building2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setViewAccount(account)}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-white font-bold hover:text-primary transition-colors">{account.company_name}</p>
                    {account.account_code && <span className="text-white/40 text-xs font-mono border border-white/15 px-2 py-0.5">{account.account_code}</span>}
                    {!account.active && <span className="text-red-400 text-[10px] font-bold tracking-widest">INACTIVE</span>}
                    {account.credit_check && (
                      <span className={`px-2 py-0.5 border text-[10px] font-bold tracking-widest uppercase ${account.credit_check.status === 'pass' ? 'text-green-400 border-green-400/40' : (account.credit_check.status === 'fail' || account.credit_check.status === 'error') ? 'text-red-400 border-red-400/40' : 'text-yellow-400 border-yellow-400/40'}`}>
                        {account.credit_check.status === 'pass' ? '✓ VERIFIED' : account.credit_check.status === 'review' ? '⚠ REVIEW' : account.credit_check.status === 'not_found' ? 'NOT FOUND' : '✗ ' + account.credit_check.status.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-1.5 text-xs text-white/40">
                    {account.contact_name && <span>{account.contact_name}</span>}
                    {account.contact_email && <a href={`mailto:${account.contact_email}`} className="hover:text-white transition-colors">{account.contact_email}</a>}
                    {account.contact_phone && <a href={`tel:${account.contact_phone}`} className="hover:text-white transition-colors">{account.contact_phone}</a>}
                    {account.payment_terms_days && <span>Net {account.payment_terms_days} days</span>}
                    {Number(account.discount_percent) > 0 && <span className="text-green-400">{account.discount_percent}% off parts</span>}
                    {account.balance != null && Number(account.balance) !== 0 && <span className="text-yellow-400">Bal: £{Number(account.balance).toFixed(2)}</span>}
                  </div>
                  {(account.address_line1 || account.address_city) && (
                    <p className="text-white/30 text-xs mt-1">{[account.address_line1, account.address_city, account.address_postcode].filter(Boolean).join(', ')}</p>
                  )}
                  {account.invited_at && (
                    <p className="text-green-400/80 text-[10px] mt-1.5 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Invited {new Date(account.invited_at).toLocaleDateString('en-GB')}</p>
                  )}
                </div>
                <div className="flex gap-2 shrink-0 items-start">
                  <button
                    onClick={() => setViewAccount(account)}
                    title="View full account history — orders, invoices, notes"
                    className="inline-flex items-center gap-1.5 border border-primary/30 bg-primary/10 hover:bg-primary hover:text-white text-primary font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5" /> VIEW
                  </button>
                  <button
                    onClick={() => setExpandedAccount(expandedAccount === account.id ? null : account.id)}
                    title="Company credit check"
                    className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/50 hover:text-white font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors"
                  >
                    <ShieldCheck className="w-3.5 h-3.5" /> CREDIT
                  </button>
                  <button
                    onClick={() => inviteAccount(account.id)}
                    disabled={inviting === account.id}
                    title={account.invited_at ? 'Resend invite email' : 'Send online account set-up email'}
                    className="inline-flex items-center gap-1.5 border border-primary/30 bg-primary/10 hover:bg-primary hover:text-white text-primary font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-50"
                  >
                    {inviting === account.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                    {account.invited_at ? 'RESEND' : 'INVITE'}
                  </button>
                  <button
                    onClick={() => exportToSage(account)}
                    disabled={exporting === account.id}
                    title="Export this customer record to a Sage 50 Customer Record import CSV"
                    className="inline-flex items-center gap-1.5 border border-cyan-400/30 bg-cyan-400/10 hover:bg-cyan-400 hover:text-black text-cyan-400 font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-50"
                  >
                    {exporting === account.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    SAGE
                  </button>
                  <button onClick={() => { setForm({ ...account }); setEditId(account.id); }} className="text-white/30 hover:text-white p-1.5 transition-colors"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => setDeleteTarget(account)} title="Delete account" className="text-white/30 hover:text-red-400 p-1.5 transition-colors"><Trash2 className="w-4 h-4" /></button>
                  <button onClick={() => setExpandedAccount(expandedAccount === account.id ? null : account.id)} className="text-white/30 hover:text-white p-1.5 transition-colors">
                    {expandedAccount === account.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              {expandedAccount === account.id && (
                <div className="border-t border-white/10 p-5">
                  <B2BCreditCheckPanel account={account} checking={checkingCredit === account.id} onRun={() => runCreditCheck(account.id)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete trade account"
        description={deleteTarget ? `This will permanently delete ${deleteTarget.company_name} and its trade record. This cannot be undone.` : ''}
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={deleteAccount}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border border-primary/30 rounded p-4 bg-[#0f0f12]">
      <p className="text-primary text-[10px] font-bold tracking-widest mb-3 pb-2 border-b border-primary/20">{title}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function F({ label, children, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}><label className="text-white/40 text-[10px] font-bold tracking-widest mb-1 block">{label}</label>{children}</div>
  );
}

function AccountForm({ form, setForm, onSave, onCancel, saving, isNew }) {
  const [codeEdited, setCodeEdited] = useState(!!form.account_code);
  const f = (k) => ({ value: form[k] || '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) });
  const num = (k) => ({ value: form[k] ?? 0, onChange: e => setForm(p => ({ ...p, [k]: e.target.value === '' ? '' : Number(e.target.value) })) });
  const deriveCode = (name) => {
    const base = String(name || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 4).toUpperCase();
    return base ? `${base}001` : '';
  };
  const onNameChange = (e) => {
    const name = e.target.value;
    setForm(p => ({ ...p, company_name: name, ...(codeEdited ? {} : { account_code: deriveCode(name) }) }));
  };
  // secondary_emails stored as array; edit via comma-separated textarea
  const emailsStr = Array.isArray(form.secondary_emails) ? form.secondary_emails.join(', ') : '';
  const onEmailsChange = (e) => setForm(p => ({ ...p, secondary_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }));

  // Section and F are defined at module scope (see below) to avoid
  // re-creating new component types on every keystroke, which remounts
  // the inputs and drops focus.

  return (
    <div className="border border-primary/20 bg-primary/[0.03] p-6 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-primary text-[10px] font-bold tracking-widest">{isNew ? 'NEW TRADE ACCOUNT' : 'EDIT ACCOUNT'}</p>
        <span className="text-white/30 text-[10px] tracking-widest">SAGE-COMPATIBLE RECORD</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEFT COLUMN */}
        <div className="space-y-4">
          <Section title="ACCOUNT DETAILS">
            <F label="A/C *"><input {...f('account_code')} onChange={e => { setCodeEdited(true); setForm(p => ({ ...p, account_code: e.target.value })); }} className="seo-input" placeholder="DEMO001" /></F>
            <F label="COMPANY NAME *"><input value={form.company_name || ''} onChange={onNameChange} className="seo-input" placeholder="Demon Tweeks" /></F>
            <F label="COMPANY REG. NUMBER"><input {...f('company_reg')} className="seo-input" placeholder="01049663" /></F>
            <F label="BALANCE (£)"><input {...num('balance')} className="seo-input" type="number" step="0.01" /></F>
            <F label="CREDIT LIMIT (£)"><input {...num('credit_limit_gbp')} className="seo-input" type="number" step="0.01" placeholder="—" /></F>
            <F label="INACTIVE"><label className="flex items-center gap-2 h-[34px]"><input type="checkbox" checked={!!form.inactive} onChange={e => setForm(p => ({ ...p, inactive: e.target.checked }))} className="w-4 h-4 accent-primary" /><span className="text-white/50 text-xs">Mark inactive in Sage</span></label></F>
            <F label="TRADE DISCOUNT (%)"><input {...num('discount_percent')} className="seo-input" type="number" min="0" max="100" step="0.5" placeholder="0" /></F>
          </Section>

          <Section title="REGISTERED ADDRESS">
            <F label="STREET 1" full><input {...f('address_line1')} className="seo-input" placeholder="75 Ash Road South" /></F>
            <F label="STREET 2" full><input {...f('address_line2')} className="seo-input" placeholder="Wrexham Industrial Estate" /></F>
            <F label="TOWN"><input {...f('address_city')} className="seo-input" /></F>
            <F label="COUNTY"><input {...f('address_county')} className="seo-input" /></F>
            <F label="POST CODE"><input {...f('address_postcode')} className="seo-input" placeholder="LL13 9UG" /></F>
            <F label="COUNTRY"><input {...f('address_country')} className="seo-input" placeholder="United Kingdom" /></F>
            <F label="VAT NUMBER"><input {...f('vat_number')} className="seo-input" /></F>
            <F label="EORI NUMBER"><input {...f('eori_number')} className="seo-input" /></F>
            <F label="UKIMS NUMBER" full><input {...f('ukims_number')} className="seo-input" /></F>
          </Section>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-4">
          <Section title="CONTACT INFORMATION">
            <F label="CONTACT NAME"><input {...f('contact_name')} className="seo-input" placeholder="Jacob Mackriell" /></F>
            <F label="TRADE CONTACT"><input {...f('trade_contact')} className="seo-input" /></F>
            <F label="TELEPHONE"><input {...f('contact_phone')} className="seo-input" placeholder="01978 663664" /></F>
            <F label="TELEPHONE 2"><input {...f('phone_2')} className="seo-input" /></F>
            <F label="FAX"><input {...f('fax')} className="seo-input" /></F>
            <F label="WEBSITE"><input {...f('website')} className="seo-input" placeholder="https://" /></F>
          </Section>

          <Section title="SOCIAL MEDIA">
            <F label="TWITTER"><input {...f('twitter')} className="seo-input" placeholder="www.twitter.com/" /></F>
            <F label="LINKEDIN"><input {...f('linkedin')} className="seo-input" placeholder="www.linkedin.com/" /></F>
            <F label="FACEBOOK"><input {...f('facebook')} className="seo-input" placeholder="www.facebook.com/" /></F>
          </Section>

          <Section title="EMAIL SETTINGS & ADDRESSES">
            <F label="EMAIL 1"><input {...f('contact_email')} className="seo-input" type="email" placeholder="JacobM@demon-tweeks.com" /></F>
            <F label="PAYMENT TERMS (DAYS)"><input {...num('payment_terms_days')} className="seo-input" type="number" /></F>
            <F label="SECONDARY EMAILS" full>
              <textarea value={emailsStr} onChange={onEmailsChange} rows={2} className="seo-input resize-y" placeholder="Email2, Email3, Email4... (comma-separated)" />
            </F>
            <F label="SEND STATEMENTS VIA EMAIL" full>
              <label className="flex items-center gap-2"><input type="checkbox" checked={!!form.email_statements} onChange={e => setForm(p => ({ ...p, email_statements: e.target.checked }))} className="w-4 h-4 accent-primary" /><span className="text-white/50 text-xs">I send letters, statements, etc. to this customer via email</span></label>
            </F>
          </Section>
        </div>
      </div>

      <div className="border border-white/10 rounded p-4 bg-[#0f0f12]">
        <p className="text-primary text-[10px] font-bold tracking-widest mb-3 pb-2 border-b border-primary/20">MEMO</p>
        <textarea {...f('notes')} rows={2} className="seo-input resize-y" placeholder="Internal notes" />
      </div>

      <div className="flex gap-3">
        <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} SAVE
        </button>
        <button onClick={onCancel} className="border border-white/10 hover:border-white/25 text-white/50 hover:text-white font-bold text-xs tracking-widest px-5 py-3 transition-colors">CANCEL</button>
      </div>
    </div>
  );
}